import * as Sentry from '@sentry/react-native';
import { SENTRY_DSN } from '@env';

import { createLogger } from '../../utils/logger';
import { scrubSensitiveFields } from './scrubber';

const logger = createLogger('Sentry');

// Default DSN for the mobile-app project. Env var override exists so
// staging / alt-builds can point at a different project without
// rebuilding. DSNs are write-only client identifiers by design and
// safe to ship in the public bundle.
const DEFAULT_DSN =
  'https://3e5024ec0dee61c66607b25292eab7ae@o4511275457642496.ingest.us.sentry.io/4511275471732736';

let initialized = false;

/**
 * Initialize Sentry. Call once, as early as possible in app startup
 * (from `index.js`, before the App component is imported). Idempotent.
 */
export function initSentry(): void {
  if (initialized) return;

  const dsn = SENTRY_DSN || DEFAULT_DSN;
  if (!dsn) {
    logger.debug('Sentry disabled — no DSN');
    return;
  }

  try {
    Sentry.init({
      dsn,
      // `environment` is what the dashboard groups by. DEV vs prod helps
      // us filter out noise from local development.
      environment: __DEV__ ? 'development' : 'production',

      // Sessions + performance monitoring both come with their own
      // privacy considerations. Turn off for the 10-user beta; enable
      // later when we have more telemetry budget.
      tracesSampleRate: 0,
      enableAutoSessionTracking: false,

      // Breadcrumbs are off by default for noisy integrations but we
      // want the ones that help diagnose a crash: navigation, XHR, and
      // console (for any `console.warn` that survived the Step-1
      // production strip). Each passes through the scrubber.
      attachScreenshot: false,
      attachViewHierarchy: false,

      // Aggressive server-side PII suppression. Sentry also has its own
      // default PII scrubbing, but we want the field-name match to run
      // on everything before the event leaves the device.
      sendDefaultPii: false,

      beforeSend: (event) => {
        try {
          return scrubSensitiveFields(event);
        } catch (err) {
          // Never crash Sentry itself. If the scrubber fails, drop the
          // event rather than send an unscrubbed copy.
          logger.error('Sentry scrubber failed — dropping event');
          return null;
        }
      },

      beforeBreadcrumb: (breadcrumb) => {
        try {
          return scrubSensitiveFields(breadcrumb);
        } catch (err) {
          logger.error('Sentry breadcrumb scrubber failed — dropping');
          return null;
        }
      },
    });

    initialized = true;
    logger.debug('Sentry initialized');
  } catch (err) {
    logger.error('Sentry init failed (non-fatal):', err);
  }
}

/**
 * Wrap the root component so any uncaught React render error becomes a
 * captured Sentry event. Pass this through to the `export default`
 * line in App.tsx instead of the raw App.
 */
export function wrapRootComponent<T>(component: T): T {
  if (!initialized) return component;
  return Sentry.wrap(component as any) as T;
}

/**
 * Imperatively capture an error. Used by errorLoggingService to mirror
 * every in-app error into Sentry in addition to the local ring buffer.
 */
export function reportError(
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  if (!initialized) return;
  try {
    const scrubbedExtra = extra ? scrubSensitiveFields(extra) : undefined;
    if (error instanceof Error) {
      Sentry.captureException(error, { extra: scrubbedExtra });
    } else {
      Sentry.captureMessage(String(error), { extra: scrubbedExtra });
    }
  } catch (err) {
    logger.error('reportError failed (non-fatal)');
  }
}

/**
 * Attach the current user's hashed identifier to future events so the
 * Sentry dashboard can group crashes by user without seeing their
 * backend ID. Expects a hash produced by
 * analytics.hashAnalyticsIdentifier so both PostHog and Sentry use the
 * same pseudonymous ID.
 */
export function setUser(hashedId: string | null): void {
  if (!initialized) return;
  try {
    if (hashedId) {
      Sentry.setUser({ id: hashedId });
    } else {
      Sentry.setUser(null);
    }
  } catch {
    /* no-op */
  }
}

/**
 * Test-only — fires a synthetic error so the dashboard can be verified
 * end-to-end. Wired from a dev-only button; never call from production
 * code paths.
 */
export function fireTestEvent(): void {
  if (!initialized) return;
  Sentry.captureException(
    new Error('Sentry smoke-test event — safe to ignore'),
  );
}
